/**
 * MockReview - consumer detail view of a finished AI Mock Interview.
 *
 * Mirrors the recruiter `InterviewReview` layout but for the candidate
 * themselves: recording + transcript + code/whiteboard artifact + AI
 * scorecard with strengths, weaknesses and detailed feedback. The
 * candidate-facing "softened explanation" field is intentionally not
 * shown -- the user is the candidate and reads the direct feedback the
 * recruiter would otherwise see.
 *
 * Differences vs recruiter side:
 *  - No Recruiter Review / override panel (mocks have no recruiter).
 *  - Different framing in copy ("Dein Feedback", "your feedback").
 *  - Audit panel is hidden (consumer doesn't need the timeline).
 *  - Recommendation chip is hidden (mocks are training, not hiring).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertTriangle,
  Clock,
  Loader2,
  Star,
  MessageCircle,
  Film,
  Code as CodeIcon,
  PenTool,
  Calculator,
  FileText as FileTextIcon,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';

import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  cleanTemplateTitle as cleanTitle,
  getMockResult,
  isPreScreenSession,
  isRecruiterDemoSession,
  normalizeEvaluationCriteria,
  resolveCriterionLabel,
  type CriterionScore,
  type EvaluationCriterion,
  type InterviewEvaluation,
  type ResultDetail,
  type TranscriptLine,
} from '../../api/interview';
import PlaybackVideo from '../../components/Interview/PlaybackVideo';
import SystemDesignViewer, {
  type SystemDesignScene,
} from '../../components/Interview/SystemDesignViewer';
import NumbersViewer from '../../components/Interview/NumbersViewer';

type Tab = 'overview' | 'recording' | 'artifact' | 'transcript';

const MockReview: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { language: lang } = useLanguage();
  const { apiCall, isLoading: authLoading, isSignedIn } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ResultDetail | null>(null);

  // `silent=true` skips the full-screen loader + the error swap so a
  // background poll can update the data without flickering the page.
  // On silent reloads we merge ONLY the evaluation / summary fields
  // and keep the previous recording URLs in place. Each backend fetch
  // produces fresh SAS URLs (Azure rotates the signature), so wholesale
  // replacing `data` would change the `recording_url` string identity
  // and reset PlaybackVideo's [src]-keyed readiness effect on every
  // poll -- causing the "Preparing playback..." overlay to flash back
  // every 6 seconds for as long as the poll runs.
  const load = useCallback(async (silent = false) => {
    if (!sessionId) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const d = await getMockResult(apiCall, sessionId);
      if (silent) {
        setData((prev) => {
          if (!prev) return d;
          return {
            ...prev,
            evaluation: d.evaluation,
            summary: d.summary,
          };
        });
      } else {
        setData(d);
      }
    } catch (e) {
      if (!silent) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiCall, sessionId]);

  useEffect(() => {
    if (authLoading) return;
    if (!isSignedIn) {
      navigate('/login');
      return;
    }
    void load();
  }, [authLoading, isSignedIn, load, navigate]);

  // Poll for the scorecard while it's still being generated server-side.
  // Mock interviews kick off a Celery task at completion time; the eval
  // row usually lands within 20-60s but a queued worker can take a few
  // minutes. We re-fetch every 6s for up to 3 minutes and stop as soon as
  // `evaluation` shows up. Outside that window the user can refresh
  // manually -- something's likely wrong on the worker side and a poll
  // loop won't fix it.
  useEffect(() => {
    if (!data) return undefined;
    if (data.evaluation) return undefined;
    if (data.summary.status !== 'completed') return undefined;

    let attempts = 0;
    const MAX_ATTEMPTS = 30;
    const interval = window.setInterval(() => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        window.clearInterval(interval);
        return;
      }
      void load(true);
    }, 6000);
    return () => window.clearInterval(interval);
  }, [data, load]);

  const T = lang === 'de' ? STRINGS_DE : STRINGS_EN;
  const isDemo = isRecruiterDemoSession(data?.session);
  const isPreScreen = isPreScreenSession(data?.session);
  const backTo = isDemo
    ? '/interviews?tab=results'
    : isPreScreen
      ? '/?tab=passport&pp=prescreen'
      : '/mock/history';
  const backLabel = isDemo ? T.backToHub : isPreScreen ? T.prescreenBack : T.backToHistory;
  const pageTitle = isPreScreen ? T.prescreenTitle : T.title;
  const privacyFooter = isPreScreen ? T.prescreenPrivacyFooter : T.privacyFooter;

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 bg-gradient-to-b from-slate-50 to-white">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <Shell back={() => navigate(backTo)} backLabel={backLabel} title={pageTitle}>
        <div className="rounded-3xl bg-white border border-rose-200 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-500 mt-0.5" />
            <div>
              <h3 className="font-semibold text-slate-900">{T.loadError}</h3>
              <p className="text-sm text-slate-600 mt-1">{error || 'Unknown error'}</p>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <InterviewResultView
      data={data}
      lang={lang}
      title={pageTitle}
      backLabel={backLabel}
      onBack={() => navigate(backTo)}
      privacyFooter={privacyFooter}
    />
  );
};

// ============================================================
// Reusable result detail view (candidate mock + recruiter pre-screen)
// ============================================================

export const InterviewResultView: React.FC<{
  data: ResultDetail;
  lang: 'de' | 'en';
  title: string;
  backLabel: string;
  onBack: () => void;
  privacyFooter: string;
}> = ({ data, lang, title, backLabel, onBack, privacyFooter }) => {
  const T = lang === 'de' ? STRINGS_DE : STRINGS_EN;
  const [tab, setTab] = useState<Tab>('overview');

  const {
    summary,
    evaluation,
    session,
    recording_url: recordingUrl,
    recording_url_screen: recordingUrlScreen,
  } = data;
  const hasCode = !!(session.code_submissions && session.code_submissions.content);
  const hasCanvas = !!(session.canvas_submissions && session.canvas_submissions.summary);
  const hasNotepad = !!(session.notepad_submissions && (session.notepad_submissions.content || '').trim().length > 0);
  const hasNumbers = !!(
    session.numbers_submissions &&
    (session.numbers_submissions.cell_count || 0) > 0
  );
  const hasArtifact = hasCode || hasCanvas || hasNumbers || hasNotepad;

  return (
    <Shell back={onBack} backLabel={backLabel} title={title}>
      <HeaderCard summary={summary} lang={lang} />

      <IntakeCard intake={summary.intake} lang={lang} />

      <div className="mt-5 space-y-5">
        <Tabs
          lang={lang}
          active={tab}
          onChange={setTab}
          hasArtifact={hasArtifact}
          artifactKind={
            [hasCode, hasCanvas, hasNumbers, hasNotepad].filter(Boolean).length > 1
              ? 'multi'
              : hasCode
                ? 'code'
                : hasCanvas
                  ? 'canvas'
                  : hasNumbers
                    ? 'numbers'
                    : 'notepad'
          }
          T={T}
        />
        {tab === 'overview' && (
          <OverviewPanel
            evaluation={evaluation}
            templateCriteria={normalizeEvaluationCriteria(data?.template?.evaluation_criteria)}
            lang={lang}
            T={T}
          />
        )}
        {tab === 'recording' && (
          <RecordingPanel
            recordingUrl={recordingUrl}
            recordingUrlScreen={recordingUrlScreen}
            lang={lang}
            T={T}
          />
        )}
        {tab === 'artifact' && (
          <ArtifactPanel
            code={session.code_submissions}
            canvas={session.canvas_submissions}
            numbers={session.numbers_submissions}
            notepad={session.notepad_submissions}
            lang={lang}
            T={T}
          />
        )}
        {tab === 'transcript' && (
          <TranscriptPanel transcript={session.transcript || []} lang={lang} T={T} />
        )}
      </div>

      <p className="mt-6 text-[11px] text-slate-400 leading-relaxed">
        {privacyFooter}
      </p>
    </Shell>
  );
};

// ============================================================
// Candidate intake (get-to-know pre-screen) -- location / desired location /
// what they seek / availability, extracted by the AI from the conversation.
// Session-scoped, never the profile.
// ============================================================

const IntakeCard: React.FC<{
  intake: ResultDetail['summary']['intake'];
  lang: 'de' | 'en';
}> = ({ intake, lang }) => {
  if (!intake) return null;
  const rows: { label: string; value?: string }[] = [
    { label: lang === 'de' ? 'Standort' : 'Based in', value: intake.current_location },
    { label: lang === 'de' ? 'Wunsch-Orte' : 'Preferred locations', value: intake.target_locations },
    { label: lang === 'de' ? 'Sucht' : 'Looking for', value: intake.seeking },
    { label: lang === 'de' ? 'Verfügbar' : 'Available', value: intake.availability },
  ].filter((r) => !!(r.value && r.value.trim()));
  if (rows.length === 0) return null;
  return (
    <div className="mt-3 rounded-3xl bg-white border border-slate-200/70 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
        {lang === 'de' ? 'Angaben der Kandidat:in' : 'Candidate details'}
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {rows.map((r) => (
          <div key={r.label}>
            <dt className="text-[11px] font-medium text-slate-400">{r.label}</dt>
            <dd className="text-sm text-slate-800 mt-0.5 leading-snug">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ============================================================
// Layout shell
// ============================================================

const Shell: React.FC<{
  back: () => void;
  backLabel: string;
  title: string;
  children: React.ReactNode;
}> = ({ back, backLabel, title, children }) => (
  <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <button
        onClick={back}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {backLabel}
      </button>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1 tracking-tight">{title}</h1>
      {children}
    </div>
  </div>
);

// ============================================================
// Header
// ============================================================

const HeaderCard: React.FC<{ summary: ResultDetail['summary']; lang: 'de' | 'en' }> = ({
  summary,
  lang,
}) => {
  const T = lang === 'de' ? STRINGS_DE : STRINGS_EN;
  const score = summary.final_score ?? summary.overall_score;
  return (
    <div className="mt-3 rounded-3xl bg-white border border-slate-200/70 p-5 sm:p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {summary.template_type || 'Simulation'}
            {summary.role_title ? <> &middot; {summary.role_title}</> : null}
            {summary.role_seniority ? <> &middot; {summary.role_seniority}</> : null}
          </p>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight mt-0.5 truncate">
            {cleanTitle(summary.template_title || 'AI interview simulation')}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Chip>
              <Clock className="w-3 h-3" />
              {summary.duration_seconds != null
                ? `${Math.floor((summary.duration_seconds || 0) / 60)} min`
                : '--'}
            </Chip>
            <Chip>{summary.persona_id}</Chip>
            <Chip tone={summary.status === 'completed' ? 'emerald' : 'amber'}>
              {summary.status}
            </Chip>
          </div>
        </div>

        {/* Score + confidence hidden from UI (data still tracked in backend) */}
      </div>
    </div>
  );
};

// ============================================================
// Tabs
// ============================================================

const Tabs: React.FC<{
  lang: 'de' | 'en';
  active: Tab;
  onChange: (t: Tab) => void;
  hasArtifact?: boolean;
  artifactKind?: 'code' | 'canvas' | 'numbers' | 'notepad' | 'multi';
  T: typeof STRINGS_EN;
}> = ({ active, onChange, hasArtifact, artifactKind, T }) => {
  const items: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview',   label: T.tabFeedback,   icon: <Star className="w-3.5 h-3.5" /> },
    { id: 'recording',  label: T.tabRecording,  icon: <Film className="w-3.5 h-3.5" /> },
    ...(hasArtifact
      ? [{
          id: 'artifact' as Tab,
          label: artifactKind === 'multi'
            ? T.tabArtifacts
            : artifactKind === 'canvas'
              ? T.tabWhiteboard
              : artifactKind === 'numbers'
                ? T.tabNumbers
                : artifactKind === 'notepad'
                  ? T.tabNotepad
                  : T.tabCode,
          icon: artifactKind === 'canvas'
            ? <PenTool className="w-3.5 h-3.5" />
            : artifactKind === 'numbers'
              ? <Calculator className="w-3.5 h-3.5" />
              : artifactKind === 'notepad'
                ? <FileTextIcon className="w-3.5 h-3.5" />
                : <CodeIcon className="w-3.5 h-3.5" />,
        }]
      : []),
    { id: 'transcript', label: T.tabTranscript, icon: <MessageCircle className="w-3.5 h-3.5" /> },
  ];
  return (
    <div className="inline-flex flex-wrap bg-white border border-slate-200/70 rounded-2xl p-1 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => onChange(it.id)}
          className={
            active === it.id
              ? 'inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold rounded-xl bg-slate-900 text-white'
              : 'inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-xl text-slate-600 hover:bg-slate-50'
          }
        >
          {it.icon}
          {it.label}
        </button>
      ))}
    </div>
  );
};

// ============================================================
// Overview / Feedback panel
// ============================================================

const OverviewPanel: React.FC<{
  evaluation: InterviewEvaluation | null;
  templateCriteria: EvaluationCriterion[];
  lang: 'de' | 'en';
  T: typeof STRINGS_EN;
}> = ({ evaluation, templateCriteria, lang, T }) => {
  if (!evaluation) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="flex items-start gap-3">
          <Loader2 className="w-5 h-5 text-amber-500 animate-spin mt-0.5" />
          <div>
            <h3 className="font-semibold text-slate-900">{T.feedbackGenerating}</h3>
            <p className="text-sm text-slate-600 mt-1">{T.feedbackGeneratingBody}</p>
          </div>
        </div>
      </div>
    );
  }

  const isAbandonedNoTranscript =
    evaluation.ai_confidence === 0 &&
    evaluation.overall_score == null &&
    (evaluation.criteria_scores == null || evaluation.criteria_scores.length === 0);

  if (isAbandonedNoTranscript) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0 mt-0.5">
            !
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{T.abandonedTitle}</h3>
            <p className="text-sm text-slate-600 mt-1">{T.abandonedBody}</p>
          </div>
        </div>
      </div>
    );
  }

  const criteriaScores = evaluation.criteria_scores || [];

  return (
    <div className="space-y-5">
      <Section title={T.criteria}>
        {criteriaScores.length === 0 ? (
          <p className="text-sm text-slate-500">{T.noCriteria}</p>
        ) : (
          <div className="space-y-3">
            {criteriaScores.map((c, i) => (
              <CriterionRow
                key={c.id || String(i)}
                criterion={c}
                label={resolveCriterionLabel(c, templateCriteria, i)}
              />
            ))}
          </div>
        )}
      </Section>

      {evaluation.strengths && (
        <Section title={T.strengths} tone="emerald">
          <Prose text={evaluation.strengths} />
        </Section>
      )}
      {evaluation.weaknesses && (
        <Section title={T.areasToDevelop} tone="amber">
          <Prose text={evaluation.weaknesses} />
        </Section>
      )}
      {evaluation.detailed_feedback && (
        <Section title={T.detailedFeedback}>
          <Prose text={evaluation.detailed_feedback} />
        </Section>
      )}
    </div>
  );
};

const CriterionRow: React.FC<{ criterion: CriterionScore; label: string }> = ({ criterion, label }) => {
  const [open, setOpen] = useState(false);
  const pct = Math.min(1, Math.max(0, (criterion.score || 0) / 5));
  return (
    <div className="border border-slate-200 rounded-2xl p-3">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-slate-900 truncate">{label}</p>
            <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-full">
              {criterion.score} / 5
            </span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-pink-500 to-orange-400"
              style={{ width: `${pct * 100}%` }}
            />
          </div>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-slate-400 hover:text-slate-600"
          aria-label={open ? 'Hide details' : 'Show details'}
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600">
          <p className="leading-relaxed">{criterion.justification}</p>
          {criterion.evidence?.length ? (
            <ul className="mt-2 space-y-1">
              {criterion.evidence.map((q, i) => (
                <li key={i} className="text-xs text-slate-500 pl-3 border-l-2 border-slate-200 italic">
                  &quot;{q}&quot;
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Recording panel
// ============================================================

const RecordingPanel: React.FC<{
  recordingUrl: string | null;
  recordingUrlScreen?: string | null;
  lang: 'de' | 'en';
  T: typeof STRINGS_EN;
}> = ({ recordingUrl, recordingUrlScreen, T }) => {
  if (!recordingUrl) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)] text-sm text-slate-500">
        <div className="flex items-start gap-3">
          <Film className="w-5 h-5 text-slate-400 mt-0.5" />
          <div>
            <p className="font-semibold text-slate-700">{T.noRecording}</p>
            <p className="mt-1 text-xs text-slate-500 leading-relaxed">{T.noRecordingBody}</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-3xl bg-black border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
      <PlaybackVideo
        src={recordingUrl}
        srcScreen={recordingUrlScreen}
        fallback={T.videoUnsupported}
      />
      <div className="px-4 py-3 text-xs text-slate-400 bg-slate-950/40 border-t border-slate-800/50">
        {T.recordingFooter}
      </div>
    </div>
  );
};

// Shared playback component: standard HTML5 video controls plus a small
// row of speed pills above the video. We keep it tiny -- 4 buttons --
// because the rest of the player UI (scrub, volume, fullscreen) is
// already perfectly handled by the browser's native controls.

// ============================================================
// Artifact panel (code or whiteboard)
// ============================================================

const ArtifactPanel: React.FC<{
  code: {
    language?: string;
    content?: string;
    char_count?: number;
    updated_at_ms?: number;
  } | null;
  canvas: {
    kind?: string;
    scene?: unknown;
    summary?: string;
    element_count?: number;
    updated_at_ms?: number;
  } | null;
  numbers: {
    rows?: number;
    cols?: number;
    cells?: Record<string, { raw?: string; value?: string | number | null }>;
    summary?: string;
    cell_count?: number;
    updated_at_ms?: number;
  } | null;
  notepad: {
    content?: string;
    char_count?: number;
    updated_at_ms?: number;
  } | null;
  lang: 'de' | 'en';
  T: typeof STRINGS_EN;
}> = ({ code, canvas, numbers, notepad, lang, T }) => {
  const hasCode = !!code?.content;
  const hasCanvas = !!canvas?.summary;
  const hasNumbers = !!(numbers && (numbers.cell_count || 0) > 0);
  const hasNotepad = !!(notepad && (notepad.content || '').trim().length > 0);
  if (!hasCode && !hasCanvas && !hasNumbers && !hasNotepad) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)] text-sm text-slate-500">
        {T.noArtifact}
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {hasCode && (
        <div className="rounded-3xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <CodeIcon className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-800">{T.finalCode}</span>
              <span className="ml-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">
                {code!.language || 'plaintext'}
              </span>
            </div>
            <span className="text-[11px] text-slate-500">
              {(code!.char_count || (code!.content || '').length).toLocaleString()} {T.chars}
            </span>
          </div>
          <pre className="text-[12.5px] leading-relaxed font-mono p-4 bg-slate-950 text-slate-100 overflow-x-auto max-h-[70vh]">
            <code>{code!.content}</code>
          </pre>
        </div>
      )}
      {hasCanvas && (() => {
        const scene = (canvas!.scene as SystemDesignScene | null | undefined) ?? null;
        const hasVisualScene = !!(scene && Array.isArray(scene.nodes) && scene.nodes.length > 0);
        return (
          <div className="rounded-3xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <PenTool className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-semibold text-slate-800">{T.whiteboardLabel}</span>
              </div>
              <span className="text-[11px] text-slate-500">
                {canvas!.element_count ?? 0} {T.elements}
              </span>
            </div>
            {hasVisualScene && (
              <div className="bg-slate-50 border-b border-slate-200">
                <SystemDesignViewer scene={scene} height={420} />
              </div>
            )}
            <details className={hasVisualScene ? 'group' : 'group [&]:open'}>
              <summary className={[
                'cursor-pointer list-none px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 hover:bg-slate-50',
                hasVisualScene ? 'border-t border-slate-200' : '',
              ].join(' ')}>
                {T.textSummary}
              </summary>
              <pre className="text-[12.5px] leading-relaxed p-4 bg-slate-50 text-slate-800 overflow-x-auto max-h-[40vh] whitespace-pre-wrap font-sans">
                {canvas!.summary}
              </pre>
            </details>
          </div>
        );
      })()}
      {hasNotepad && (
        <div className="rounded-3xl bg-[#fffbe6] border border-yellow-300/60 overflow-hidden shadow-[0_2px_8px_rgba(217,119,6,0.06)]">
          <div className="flex items-center justify-between px-4 py-3 bg-yellow-100/70 border-b border-yellow-300/60">
            <div className="flex items-center gap-2">
              <FileTextIcon className="w-4 h-4 text-yellow-800" />
              <span className="text-sm font-semibold text-yellow-900">{T.notepadLabel}</span>
            </div>
            <span className="text-[11px] text-yellow-800/70">
              {(notepad!.char_count ?? (notepad!.content || '').length).toLocaleString()} {T.chars}
            </span>
          </div>
          <pre className="text-[13px] leading-6 px-6 py-4 text-amber-950 whitespace-pre-wrap font-[ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace] max-h-[60vh] overflow-y-auto">
            {notepad!.content}
          </pre>
        </div>
      )}
      {hasNumbers && (
        <div className="rounded-3xl bg-white border border-slate-200/70 overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-semibold text-slate-800">{T.numbersLabel}</span>
            </div>
            <span className="text-[11px] text-slate-500">
              {numbers!.cell_count ?? 0} {T.cells}
            </span>
          </div>
          <div className="bg-slate-950 p-1">
            <NumbersViewer snapshot={numbers!} lang={lang} height={420} />
          </div>
          {numbers!.summary && (
            <details className="group">
              <summary className="cursor-pointer list-none px-4 py-2 text-[11px] uppercase tracking-wider font-semibold text-slate-500 hover:bg-slate-50 border-t border-slate-200">
                {T.textSummary}
              </summary>
              <pre className="text-[12.5px] leading-relaxed p-4 bg-slate-50 text-slate-800 overflow-x-auto max-h-[40vh] whitespace-pre-wrap font-mono">
                {numbers!.summary}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Transcript panel
// ============================================================

const TranscriptPanel: React.FC<{
  transcript: TranscriptLine[];
  lang: 'de' | 'en';
  T: typeof STRINGS_EN;
}> = ({ transcript, T }) => {
  if (!transcript || transcript.length === 0) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200/70 p-6 shadow-[0_2px_8px_rgba(15,23,42,0.04)] text-sm text-slate-500">
        {T.noTranscript}
      </div>
    );
  }
  return (
    <div className="rounded-3xl bg-white border border-slate-200/70 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)] space-y-2 max-h-[640px] overflow-y-auto">
      {transcript.map((l, i) => (
        <div key={i} className={`flex ${l.speaker === 'candidate' ? 'justify-end' : 'justify-start'}`}>
          <div className={
            l.speaker === 'candidate'
              ? 'max-w-[78%] px-3.5 py-2 rounded-2xl rounded-br-md bg-gradient-to-br from-pink-500/10 to-orange-400/10 text-slate-800 border border-pink-200/50 text-sm leading-relaxed'
              : l.speaker === 'system'
              ? 'max-w-[78%] px-3 py-2 rounded-2xl bg-amber-50 text-amber-700 border border-amber-200 text-xs'
              : 'max-w-[78%] px-3.5 py-2 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-200 text-slate-800 text-sm leading-relaxed'
          }>
            {l.text}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// Primitives
// ============================================================

const Section: React.FC<{ title: string; tone?: 'emerald' | 'amber'; children: React.ReactNode }> = ({
  title,
  tone,
  children,
}) => (
  <div className="rounded-3xl bg-white border border-slate-200/70 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
    <p className={[
      'text-[10px] uppercase tracking-wider font-semibold mb-3',
      tone === 'emerald' ? 'text-emerald-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-500',
    ].join(' ')}>{title}</p>
    {children}
  </div>
);

const Prose: React.FC<{ text: string }> = ({ text }) => (
  <p className="text-sm whitespace-pre-line leading-relaxed text-slate-700">{text}</p>
);

const Chip: React.FC<{ children: React.ReactNode; tone?: 'emerald' | 'amber' | 'slate' }> = ({
  children,
  tone = 'slate',
}) => {
  const cls =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : tone === 'amber'
      ? 'bg-amber-50 text-amber-700 border border-amber-200'
      : 'bg-slate-50 text-slate-700 border border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {children}
    </span>
  );
};

// cleanTitle is imported at the top of the file (cleanTemplateTitle).

// ============================================================
// i18n strings
// ============================================================

const STRINGS_EN = {
  title: 'Your simulation feedback',
  backToHistory: 'Back to your simulations',
  backToHub: 'Back to interview hub',
  prescreenTitle: 'Your Intro feedback',
  prescreenBack: 'Back to your Passport',
  prescreenPrivacyFooter:
    'This Intro result is yours. Recruiters only see it if you turn on visibility in your Passport. Recording, transcript and feedback are auto-deleted after 90 days.',
  loadError: 'Could not load this simulation',
  privacyFooter:
    'Private to you. Recruiters and companies never see this simulation. Recording, transcript and feedback are auto-deleted after 90 days.',
  scoreLabel: 'AI score',
  confidence: 'AI confidence',
  tabFeedback: 'Feedback',
  tabRecording: 'Recording',
  tabCode: 'Code',
  tabWhiteboard: 'Whiteboard',
  tabNumbers: 'Numbers',
  tabNotepad: 'Notepad',
  tabArtifacts: 'Artifacts',
  tabTranscript: 'Transcript',
  feedbackGenerating: 'Feedback is being generated',
  feedbackGeneratingBody:
    'The AI is reviewing your transcript and writing structured feedback. This usually takes less than a minute.',
  abandonedTitle: 'No transcript - simulation was abandoned',
  abandonedBody:
    'No audio was captured for this simulation. Most likely the tab was closed before the first answer.',
  criteria: 'Criteria',
  noCriteria: 'No criteria scored.',
  strengths: 'What worked well',
  areasToDevelop: 'What to refine next time',
  detailedFeedback: 'Detailed feedback',
  noRecording: 'No recording available',
  noRecordingBody:
    'Either no recording was captured, the simulation was abandoned before the first chunk, or the 90 day retention window expired.',
  recordingFooter: 'Recording contains your video + audio + screen and the AI voice. Streaming-only - no download.',
  videoUnsupported: 'Your browser does not support the video tag.',
  finalCode: 'Your final code',
  chars: 'chars',
  whiteboardLabel: 'Your whiteboard',
  elements: 'elements',
  numbersLabel: 'Your calculations',
  cells: 'cells',
  notepadLabel: 'Your notes',
  textSummary: 'Text summary (what the AI saw)',
  noArtifact: 'No code, whiteboard, numbers or notepad artifact for this simulation.',
  noTranscript: 'No transcript stored for this simulation.',
};

const STRINGS_DE = {
  title: 'Dein Simulations-Feedback',
  backToHistory: 'Zurück zu deinen Simulationen',
  backToHub: 'Zurück zum Interview-Hub',
  prescreenTitle: 'Dein Intro-Feedback',
  prescreenBack: 'Zurück zu deinem Passport',
  prescreenPrivacyFooter:
    'Dieses Intro-Ergebnis gehört dir. Recruiter:innen sehen es nur, wenn du die Sichtbarkeit in deinem Passport freigibst. Aufnahme, Transkript und Feedback werden nach 90 Tagen automatisch gelöscht.',
  loadError: 'Konnte diese Simulation nicht laden',
  privacyFooter:
    'Privat für dich. Recruiter:innen und Unternehmen sehen das nie. Aufnahme, Transkript und Feedback werden nach 90 Tagen automatisch gelöscht.',
  scoreLabel: 'KI-Score',
  confidence: 'KI-Konfidenz',
  tabFeedback: 'Feedback',
  tabRecording: 'Aufnahme',
  tabCode: 'Code',
  tabWhiteboard: 'Whiteboard',
  tabNumbers: 'Zahlen',
  tabNotepad: 'Notizblock',
  tabArtifacts: 'Artefakte',
  tabTranscript: 'Transkript',
  feedbackGenerating: 'Dein Feedback wird erstellt',
  feedbackGeneratingBody:
    'Die KI bewertet dein Transkript und schreibt strukturiertes Feedback. Das dauert in der Regel weniger als eine Minute.',
  abandonedTitle: 'Kein Transkript - Simulation wurde abgebrochen',
  abandonedBody:
    'Für diese Simulation wurde kein Audio aufgenommen. Wahrscheinlich wurde der Tab geschlossen bevor du das erste Mal gesprochen hast.',
  criteria: 'Kriterien',
  noCriteria: 'Keine Kriterien bewertet.',
  strengths: 'Was gut lief',
  areasToDevelop: 'Was du beim nächsten Mal schärfer machen kannst',
  detailedFeedback: 'Ausführliches Feedback',
  noRecording: 'Keine Aufnahme verfügbar',
  noRecordingBody:
    'Entweder wurde nichts aufgezeichnet, die Simulation wurde vor dem ersten Chunk abgebrochen, oder die 90-Tage-Frist ist abgelaufen.',
  recordingFooter:
    'Aufnahme enthält dein Video + Audio + Bildschirm und die KI-Stimme. Nur Streaming - kein Download.',
  videoUnsupported: 'Dein Browser unterstützt das Video-Tag nicht.',
  finalCode: 'Dein finaler Code',
  chars: 'Zeichen',
  whiteboardLabel: 'Dein Whiteboard',
  elements: 'Elemente',
  numbersLabel: 'Deine Rechnung',
  cells: 'Zellen',
  notepadLabel: 'Deine Notizen',
  textSummary: 'Text-Zusammenfassung (was die KI gesehen hat)',
  noArtifact: 'Kein Code-, Whiteboard-, Zahlen- oder Notiz-Artefakt für diese Simulation.',
  noTranscript: 'Kein Transkript für diese Simulation gespeichert.',
};

export default MockReview;
